import { Bell, Check, LoaderCircle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { clinicalTrialsService } from "../../api/clinicalTrialsService";
import type { EligibilityNoteNotification } from "../../api/clinicalTrialsApiTypes";

interface EligibilityNotesNotificationModalProps {
  trialId: string;
  mrn: string;
  patientName: string;
  onClose: () => void;
  onOpenNotification: (notification: EligibilityNoteNotification) => void;
  onUnreadCountChange: (unreadCount: number) => void;
}

function formatNotificationTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

/** Modal containing the authenticated user's Notes activity for one patient. */
export function EligibilityNotesNotificationModal({
  trialId,
  mrn,
  patientName,
  onClose,
  onOpenNotification,
  onUnreadCountChange,
}: EligibilityNotesNotificationModalProps) {
  const [notifications, setNotifications] = useState<EligibilityNoteNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    clinicalTrialsService
      .getEligibilityNotifications(trialId, mrn, controller.signal)
      .then((response) => setNotifications(response.notifications))
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(
            reason instanceof Error
              ? reason.message
              : "Failed to load patient notifications",
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [mrn, trialId]);

  const handleNotificationClick = async (
    notification: EligibilityNoteNotification,
  ): Promise<void> => {
    if (!notification.read_at) {
      try {
        await clinicalTrialsService.markEligibilityNotificationRead(
          trialId,
          mrn,
          notification.notification_id,
        );
        setNotifications((current) =>
          current.map((item) =>
            item.notification_id === notification.notification_id
              ? { ...item, read_at: new Date().toISOString() }
              : item,
          ),
        );
        onUnreadCountChange(
          notifications.filter(
            (item) => item.notification_id !== notification.notification_id && !item.read_at,
          ).length,
        );
      } catch (reason: unknown) {
        setError(
          reason instanceof Error
            ? reason.message
            : "Failed to mark notification as read",
        );
        return;
      }
    }
    onOpenNotification(notification);
  };

  const clearAllNotifications = async (): Promise<void> => {
    if (isClearing || !notifications.some((item) => !item.read_at)) return;
    setIsClearing(true);
    setError(null);
    try {
      await clinicalTrialsService.clearEligibilityNotifications(trialId, mrn);
      setNotifications((current) =>
        current.map((item) => ({
          ...item,
          read_at: item.read_at ?? new Date().toISOString(),
        })),
      );
      onUnreadCountChange(0);
    } catch (reason: unknown) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Failed to clear patient notifications",
      );
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="mt-16 flex max-h-[min(36rem,calc(100vh-8rem))] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="eligibility-notifications-title"
      >
        <header className="flex items-center justify-between border-b border-primaryGray-14 px-4 py-3">
          <div className="min-w-0">
            <h2
              id="eligibility-notifications-title"
              className="truncate text-sm font-semibold text-primaryGray-1"
            >
              Patient Updates{patientName ? ` - ${patientName}` : ""}
            </h2>
            <p className="text-x-tiny text-primaryGray-3">MRN: {mrn}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="text-x-tiny font-semibold text-tertiaryBlue-4 disabled:opacity-40"
              onClick={() => void clearAllNotifications()}
              disabled={isClearing || !notifications.some((item) => !item.read_at)}
            >
              {isClearing ? "Clearing..." : "Clear All"}
            </button>
            <button
              type="button"
              className="rounded p-1 text-primaryGray-3 hover:bg-primaryGray-16"
              aria-label="Close patient notifications"
              onClick={onClose}
            >
              <X size={18} />
            </button>
          </div>
        </header>
        {error ? (
          <p className="border-b border-statusRed-text/20 bg-statusRed-text/5 px-4 py-2 text-x-tiny text-statusRed-text">
            {error}
          </p>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 p-8 text-x-tiny text-primaryGray-3">
              <LoaderCircle size={16} className="animate-spin" /> Loading updates...
            </div>
          ) : !notifications.length ? (
            <div className="flex flex-col items-center gap-2 p-10 text-center text-x-tiny text-primaryGray-3">
              <Bell size={22} />
              <span>No patient updates yet.</span>
            </div>
          ) : (
            notifications.map((notification) => {
              const isUnread = !notification.read_at;
              return (
                <button
                  type="button"
                  key={notification.notification_id}
                  className={`flex w-full items-start gap-3 border-b border-primaryGray-14 px-4 py-3 text-left hover:bg-primaryGray-16 ${isUnread ? "bg-blue-50" : "bg-white"}`}
                  onClick={() => void handleNotificationClick(notification)}
                >
                  <span className="mt-0.5 text-tertiaryBlue-4" aria-hidden="true">
                    {isUnread ? <Bell size={16} fill="currentColor" /> : <Check size={16} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block text-xs text-primaryGray-1 ${isUnread ? "font-semibold" : "font-normal"}`}>
                      {notification.title}
                    </span>
                    <span className="mt-1 block text-x-tiny text-primaryGray-3">
                      {notification.preview}
                    </span>
                    <span className="mt-1 block text-[0.65rem] text-primaryGray-5">
                      {formatNotificationTime(notification.created_at)}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
